import { useState, useEffect, useCallback } from 'react';
import { TopBar } from '../components/layout/TopBar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import {
  Crown, Star, Users, Shield, Plus, Edit, Trash2, CheckCircle,
  RefreshCw, Zap, Lock, Calendar, Loader2, LayoutGrid, DollarSign
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const authHeader = () => ({ 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` });

const EMPTY_PLAN = {
  name: '', price: '', price_period: 'mes', color: 'from-orange-400 to-orange-600',
  icon: 'Crown', featured: false, active: true, benefits: [''],
  advance_hours: 48, discount_pct: 10, description: ''
};

const EMPTY_MEMBER = { name: '', email: '', phone: '', plan_id: '', start_date: new Date().toISOString().slice(0, 10), notes: '' };

const ICON_MAP = { Crown, Star, Shield };
const COLOR_OPTIONS = [
  { value: 'from-gray-500 to-gray-700', label: 'Gris' },
  { value: 'from-orange-400 to-orange-600', label: 'Naranja' },
  { value: 'from-gray-900 to-black', label: 'Negro' },
  { value: 'from-indigo-500 to-indigo-700', label: 'Índigo' },
  { value: 'from-emerald-500 to-emerald-700', label: 'Verde' },
  { value: 'from-rose-500 to-rose-700', label: 'Rosa' },
];
const ICON_OPTIONS = ['Crown', 'Star', 'Shield'];

export default function GuaranteedAccess() {
  /* Plans */
  const [plans, setPlans] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [planModal, setPlanModal] = useState({ open: false, plan: null });
  const [planForm, setPlanForm] = useState(EMPTY_PLAN);
  const [savingPlan, setSavingPlan] = useState(false);

  /* Members */
  const [members, setMembers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(true);
  const [memberModal, setMemberModal] = useState({ open: false, member: null });
  const [memberForm, setMemberForm] = useState(EMPTY_MEMBER);
  const [savingMember, setSavingMember] = useState(false);

  /* Load Plans */
  const loadPlans = useCallback(async () => {
    setLoadingPlans(true);
    try {
      const res = await fetch(`${API_URL}/api/nuevas_funciones/acceso-garantizado/plans`, { headers: authHeader() });
      if (res.ok) setPlans(await res.json());
    } catch { }
    finally { setLoadingPlans(false); }
  }, []);

  /* Load Members */
  const loadMembers = useCallback(async () => {
    setLoadingMembers(true);
    try {
      const res = await fetch(`${API_URL}/api/memberships/subscribers`, { headers: authHeader() });
      if (res.ok) {
        const data = await res.json();
        const guaranteed = Array.isArray(data)
          ? data.filter(m => m.plan_type === 'acceso_garantizado' || ['insider', 'elite', 'vip'].includes(m.tier))
          : [];
        setMembers(guaranteed);
      }
    } catch { }
    finally { setLoadingMembers(false); }
  }, []);

  useEffect(() => { loadPlans(); loadMembers(); }, [loadPlans, loadMembers]);

  /* Plan CRUD */
  const openPlanModal = (plan = null) => {
    setPlanForm(plan ? {
      ...plan,
      benefits: Array.isArray(plan.benefits) && plan.benefits.length > 0 ? plan.benefits : [''],
    } : EMPTY_PLAN);
    setPlanModal({ open: true, plan });
  };

  const savePlan = async () => {
    if (!planForm.name.trim() || !planForm.price.trim()) {
      toast.error('Nombre y precio son obligatorios'); return;
    }
    setSavingPlan(true);
    try {
      const payload = { ...planForm, benefits: planForm.benefits.filter(b => b.trim()) };
      const url = planModal.plan
        ? `${API_URL}/api/nuevas_funciones/acceso-garantizado/plans/${planModal.plan.id}`
        : `${API_URL}/api/nuevas_funciones/acceso-garantizado/plans`;
      const method = planModal.plan ? 'PUT' : 'POST';
      const res = await fetch(url, { method, headers: authHeader(), body: JSON.stringify(payload) });
      if (!res.ok) throw new Error();
      toast.success(planModal.plan ? 'Plan actualizado' : 'Plan creado');
      setPlanModal({ open: false, plan: null });
      loadPlans();
    } catch { toast.error('Error al guardar el plan'); }
    finally { setSavingPlan(false); }
  };

  const deletePlan = async (id) => {
    if (!window.confirm('¿Eliminar este plan?')) return;
    await fetch(`${API_URL}/api/nuevas_funciones/acceso-garantizado/plans/${id}`, { method: 'DELETE', headers: authHeader() });
    toast.success('Plan eliminado'); loadPlans();
  };

  /* Benefit helpers */
  const addBenefit = () => setPlanForm(p => ({ ...p, benefits: [...p.benefits, ''] }));
  const removeBenefit = (i) => setPlanForm(p => ({ ...p, benefits: p.benefits.filter((_, idx) => idx !== i) }));
  const updateBenefit = (i, v) => setPlanForm(p => ({ ...p, benefits: p.benefits.map((b, idx) => idx === i ? v : b) }));

  /* Member CRUD */
  const openMemberModal = (member = null) => {
    setMemberForm(member ? { ...member } : { ...EMPTY_MEMBER, plan_id: plans[0]?.id || '' });
    setMemberModal({ open: true, member });
  };

  const saveMember = async () => {
    if (!memberForm.name || !memberForm.email) { toast.error('Nombre y email son obligatorios'); return; }
    setSavingMember(true);
    try {
      const selectedPlan = plans.find(p => p.id === memberForm.plan_id);
      const payload = { ...memberForm, plan_type: 'acceso_garantizado', status: 'active', tier: selectedPlan?.key || 'elite' };
      const url = memberModal.member
        ? `${API_URL}/api/memberships/subscribers/${memberModal.member.id}`
        : `${API_URL}/api/memberships/subscribers`;
      const method = memberModal.member ? 'PUT' : 'POST';
      const res = await fetch(url, { method, headers: authHeader(), body: JSON.stringify(payload) });
      if (!res.ok) throw new Error();
      toast.success(memberModal.member ? 'Socio actualizado' : 'Socio registrado');
      setMemberModal({ open: false, member: null });
      loadMembers();
    } catch { toast.error('Error al guardar'); }
    finally { setSavingMember(false); }
  };

  const deleteMember = async (id) => {
    if (!window.confirm('¿Cancelar membresía de este socio?')) return;
    await fetch(`${API_URL}/api/memberships/subscribers/${id}`, { method: 'DELETE', headers: authHeader() });
    toast.success('Membresía cancelada'); loadMembers();
  };

  const getPlanInfo = (member) => {
    const byId = plans.find(p => p.id === member.plan_id);
    const byTier = plans.find(p => p.key === member.tier);
    return byId || byTier || { name: member.tier || 'Plan', color: 'from-gray-400 to-gray-600', icon: 'Star' };
  };

  const stats = [
    { label: 'Socios activos', value: members.filter(m => m.status === 'active').length, icon: Users, color: 'text-orange-500', bg: 'bg-orange-50' },
    { label: 'Planes activos', value: plans.filter(p => p.active).length, icon: LayoutGrid, color: 'text-indigo-500', bg: 'bg-indigo-50' },
    { label: 'Ingresos estimados', value: members.length > 0 ? `${members.length} socios` : '—', icon: DollarSign, color: 'text-green-600', bg: 'bg-green-50' },
    { label: 'Retención', value: members.length > 0 ? `${Math.round((members.filter(m => m.status === 'active').length / members.length) * 100)}%` : '—', icon: Crown, color: 'text-amber-500', bg: 'bg-amber-50' },
  ];

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <TopBar title="Acceso Garantizado" helpSection="acceso-garantizado" />
      <div className="flex-1 overflow-y-auto p-6 space-y-6">

        {/* Hero */}
        <div className="bg-gradient-to-r from-black to-gray-800 text-white rounded-2xl p-6 flex items-center justify-between flex-wrap gap-4">
          <div>
            <p className="text-orange-400 text-sm font-semibold uppercase tracking-wider mb-1">Club de Acceso</p>
            <h2 className="text-2xl font-black">Membresías con Mesa Garantizada</h2>
            <p className="text-gray-400 mt-1">Tus mejores clientes nunca esperan — acceso VIP asegurado</p>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => openPlanModal()} className="bg-white text-black hover:bg-gray-100 gap-2">
              <Plus className="w-4 h-4" /> Nuevo plan
            </Button>
            <Button onClick={() => openMemberModal()} className="bg-orange-500 hover:bg-orange-600 text-white gap-2">
              <Plus className="w-4 h-4" /> Agregar socio
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {stats.map((s, i) => (
            <Card key={i}><CardContent className="p-4 flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl ${s.bg} flex items-center justify-center`}>
                <s.icon className={`w-5 h-5 ${s.color}`} />
              </div>
              <div><p className="text-2xl font-black">{s.value}</p><p className="text-xs text-gray-500">{s.label}</p></div>
            </CardContent></Card>
          ))}
        </div>

        <Tabs defaultValue="plans">
          <TabsList>
            <TabsTrigger value="plans">Planes ({plans.length})</TabsTrigger>
            <TabsTrigger value="members">Socios ({members.length})</TabsTrigger>
          </TabsList>

          {/* PLANES TAB */}
          <TabsContent value="plans" className="mt-6">
            {loadingPlans ? (
              <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 text-orange-500 animate-spin" /></div>
            ) : plans.length === 0 ? (
              <div className="text-center py-16 text-gray-400">
                <LayoutGrid className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p className="font-medium text-gray-600">Sin planes configurados</p>
                <p className="text-sm mb-4">Crea los planes de membresía que ofrecerás a tus clientes VIP</p>
                <Button onClick={() => openPlanModal()} className="bg-orange-500 hover:bg-orange-600 text-white gap-2">
                  <Plus className="w-4 h-4" /> Crear primer plan
                </Button>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {plans.map(plan => {
                  const IconComp = ICON_MAP[plan.icon] || Crown;
                  return (
                    <div key={plan.id} className={`relative rounded-2xl overflow-hidden ${plan.featured ? 'ring-2 ring-orange-500' : 'border border-gray-200'}`}>
                      {plan.featured && (
                        <div className="absolute top-0 inset-x-0 text-center py-1 bg-orange-500 text-white text-xs font-bold">
                          PLAN DESTACADO
                        </div>
                      )}
                      {!plan.active && (
                        <div className="absolute top-0 inset-x-0 text-center py-1 bg-gray-400 text-white text-xs font-bold">
                          INACTIVO
                        </div>
                      )}
                      <div className={`bg-gradient-to-br ${plan.color} text-white p-6 ${plan.featured || !plan.active ? 'pt-10' : ''}`}>
                        <IconComp className="w-10 h-10 mb-3 opacity-90" />
                        <h3 className="text-2xl font-black">{plan.name}</h3>
                        <p className="text-white/70 text-sm mt-1">{plan.price} / {plan.price_period}</p>
                        {plan.description && <p className="text-white/60 text-xs mt-2">{plan.description}</p>}
                      </div>
                      <div className="bg-white p-5 space-y-2">
                        {(plan.benefits || []).map((b, i) => (
                          <div key={i} className="flex items-start gap-2">
                            <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                            <p className="text-sm text-gray-700">{b}</p>
                          </div>
                        ))}
                        <div className="flex gap-2 pt-3">
                          <Button size="sm" variant="outline" className="flex-1" onClick={() => openPlanModal(plan)}>
                            <Edit className="w-3.5 h-3.5 mr-1" /> Editar
                          </Button>
                          <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-600" onClick={() => deletePlan(plan.id)}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Features info */}
            {plans.length > 0 && (
              <div className="mt-8 grid md:grid-cols-3 gap-4">
                {[
                  { icon: Lock, title: 'Mesa garantizada', desc: 'Los socios siempre tienen mesa disponible. Sin tiempo de espera, sin cancelaciones.' },
                  { icon: Zap, title: 'Reservación express', desc: 'Sistema de reservación prioritaria con ventanas de tiempo exclusivas para socios.' },
                  { icon: Calendar, title: 'Acceso anticipado', desc: 'Los socios pueden reservar antes que el público general, según el plan.' },
                ].map((item, i) => (
                  <Card key={i}><CardContent className="p-5 flex gap-3">
                    <item.icon className="w-8 h-8 text-orange-500 flex-shrink-0" />
                    <div><h4 className="font-bold text-gray-900 mb-1">{item.title}</h4><p className="text-sm text-gray-500">{item.desc}</p></div>
                  </CardContent></Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* SOCIOS TAB */}
          <TabsContent value="members" className="mt-6">
            {loadingMembers ? (
              <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 text-orange-500 animate-spin" /></div>
            ) : members.length === 0 ? (
              <div className="text-center py-16 text-gray-400">
                <Crown className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p className="font-medium">No hay socios de acceso garantizado todavía</p>
                <p className="text-sm">Registra a tus mejores clientes para darles acceso VIP permanente</p>
                <Button className="mt-4 bg-orange-500 hover:bg-orange-600 text-white" onClick={() => openMemberModal()}>
                  <Plus className="w-4 h-4 mr-1" /> Primer socio
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {members.map(m => {
                  const plan = getPlanInfo(m);
                  const IconComp = ICON_MAP[plan.icon] || Crown;
                  return (
                    <Card key={m.id}><CardContent className="p-4">
                      <div className="flex items-center justify-between flex-wrap gap-3">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${plan.color} flex items-center justify-center text-white`}>
                            <IconComp className="w-5 h-5" />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="font-bold text-gray-900">{m.name}</p>
                              <Badge className="bg-orange-100 text-orange-700 text-xs">{plan.name}</Badge>
                              {m.status === 'active'
                                ? <Badge className="bg-emerald-100 text-emerald-700 text-xs">Activo</Badge>
                                : <Badge className="bg-gray-100 text-gray-500 text-xs">{m.status}</Badge>}
                            </div>
                            <p className="text-sm text-gray-500">{m.email}{m.phone ? ` · ${m.phone}` : ''}</p>
                            {m.start_date && <p className="text-xs text-gray-400">Socio desde: {m.start_date}</p>}
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" variant="ghost" onClick={() => openMemberModal(m)}><Edit className="w-3.5 h-3.5" /></Button>
                          <Button size="sm" variant="ghost" className="text-red-500" onClick={() => deleteMember(m.id)}><Trash2 className="w-3.5 h-3.5" /></Button>
                        </div>
                      </div>
                      {m.notes && <p className="text-xs text-gray-400 mt-2 bg-gray-50 rounded px-3 py-1">📝 {m.notes}</p>}
                    </CardContent></Card>
                  );
                })}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* ─── PLAN MODAL ─── */}
      <Dialog open={planModal.open} onOpenChange={o => setPlanModal(p => ({ ...p, open: o }))}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{planModal.plan ? 'Editar plan' : 'Nuevo plan de membresía'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label>Nombre del plan *</Label>
                <Input value={planForm.name} onChange={e => setPlanForm(p => ({ ...p, name: e.target.value }))} placeholder="ej. Elite, VIP Black..." />
              </div>
              <div>
                <Label>Precio *</Label>
                <Input value={planForm.price} onChange={e => setPlanForm(p => ({ ...p, price: e.target.value }))} placeholder="ej. $599" />
              </div>
              <div>
                <Label>Periodo</Label>
                <Select value={planForm.price_period} onValueChange={v => setPlanForm(p => ({ ...p, price_period: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {['mes', 'año', 'semana', 'única vez'].map(v => <SelectItem key={v} value={v}>{v}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Ícono</Label>
                <Select value={planForm.icon} onValueChange={v => setPlanForm(p => ({ ...p, icon: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ICON_OPTIONS.map(v => <SelectItem key={v} value={v}>{v}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Color</Label>
                <Select value={planForm.color} onValueChange={v => setPlanForm(p => ({ ...p, color: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {COLOR_OPTIONS.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Descripción corta</Label>
              <Textarea value={planForm.description} onChange={e => setPlanForm(p => ({ ...p, description: e.target.value }))} rows={2} placeholder="Describe brevemente este nivel..." />
            </div>

            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Switch checked={planForm.featured} onCheckedChange={v => setPlanForm(p => ({ ...p, featured: v }))} />
                <Label>Plan destacado</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={planForm.active} onCheckedChange={v => setPlanForm(p => ({ ...p, active: v }))} />
                <Label>Plan activo</Label>
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Beneficios</Label>
                <Button type="button" size="sm" variant="ghost" onClick={addBenefit} className="h-7 text-xs gap-1">
                  <Plus className="w-3 h-3" /> Agregar
                </Button>
              </div>
              <div className="space-y-2">
                {planForm.benefits.map((b, i) => (
                  <div key={i} className="flex gap-2">
                    <Input value={b} onChange={e => updateBenefit(i, e.target.value)} placeholder={`Beneficio ${i + 1}`} className="text-sm" />
                    {planForm.benefits.length > 1 && (
                      <Button type="button" size="icon" variant="ghost" onClick={() => removeBenefit(i)} className="h-9 w-9 flex-shrink-0 text-red-400 hover:text-red-600">
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPlanModal(p => ({ ...p, open: false }))}>Cancelar</Button>
            <Button onClick={savePlan} disabled={savingPlan} className="bg-orange-500 hover:bg-orange-600 text-white">
              {savingPlan ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Guardar plan'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── MEMBER MODAL ─── */}
      <Dialog open={memberModal.open} onOpenChange={o => setMemberModal(p => ({ ...p, open: o }))}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{memberModal.member ? 'Editar socio' : 'Nuevo socio de acceso garantizado'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div><Label>Nombre completo *</Label><Input value={memberForm.name} onChange={e => setMemberForm(p => ({ ...p, name: e.target.value }))} /></div>
            <div><Label>Email *</Label><Input type="email" value={memberForm.email} onChange={e => setMemberForm(p => ({ ...p, email: e.target.value }))} /></div>
            <div><Label>Teléfono / WhatsApp</Label><Input value={memberForm.phone} onChange={e => setMemberForm(p => ({ ...p, phone: e.target.value }))} placeholder="+52 55..." /></div>
            <div>
              <Label>Plan de membresía</Label>
              {plans.length > 0 ? (
                <Select value={memberForm.plan_id} onValueChange={v => setMemberForm(p => ({ ...p, plan_id: v }))}>
                  <SelectTrigger><SelectValue placeholder="Seleccionar plan" /></SelectTrigger>
                  <SelectContent>
                    {plans.map(pl => <SelectItem key={pl.id} value={pl.id}>{pl.name} — {pl.price}/{pl.price_period}</SelectItem>)}
                  </SelectContent>
                </Select>
              ) : (
                <p className="text-xs text-amber-600 mt-1">Crea planes primero en la pestaña Planes.</p>
              )}
            </div>
            <div><Label>Fecha de inicio</Label><Input type="date" value={memberForm.start_date} onChange={e => setMemberForm(p => ({ ...p, start_date: e.target.value }))} /></div>
            <div><Label>Notas especiales</Label><Textarea value={memberForm.notes} onChange={e => setMemberForm(p => ({ ...p, notes: e.target.value }))} rows={2} placeholder="Preferencias, alérgenos, ocasiones especiales..." /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMemberModal(p => ({ ...p, open: false }))}>Cancelar</Button>
            <Button onClick={saveMember} disabled={savingMember} className="bg-orange-500 hover:bg-orange-600 text-white">
              {savingMember ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Guardar socio'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
